import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ArrowRight, Loader2, Stethoscope, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignupSuccessPage() {
  const [params] = useSearchParams();
  const loc = useLocation();
  const slug = params.get('slug') || '';
  const sid = params.get('session_id');
  const [tenant, setTenant] = useState(loc.state?.tenant || null);
  const [adminEmail] = useState(loc.state?.admin_email || '');
  const [polling, setPolling] = useState(false);
  const [paid, setPaid] = useState(false);
  const [copied, setCopied] = useState(false);
  const loginUrl =
    loc.state?.login_url || `${window.location.origin}/login?tenant=${encodeURIComponent(slug)}`;

  // Re-fetch tenant info (especially after Stripe redirect)
  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/api/tenant/resolve`, { params: { slug } })
      .then(r => setTenant(t => t || {
        slug: r.data.slug, name: r.data.name, plan_code: r.data.plan_code,
      }))
      .catch(() => {});
  }, [slug]);

  // Stripe poll-back
  useEffect(() => {
    if (!sid) return;
    setPolling(true);
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const r = await axios.get(`${API}/api/saas/billing/checkout/${sid}`);
        if (r.data.payment_status === 'paid') { setPaid(true); setPolling(false); return; }
        if (r.data.status === 'expired') { setPolling(false); return; }
      } catch (_) {}
      if (n < 10) setTimeout(tick, 2000);
      else setPolling(false);
    };
    tick();
  }, [sid]);

  const copyLink = () => {
    navigator.clipboard?.writeText(loginUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-zinc-50 to-blue-50">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-6 py-3.5 flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-blue-700" />
          <span className="font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>eCare 360</span>
        </div>
      </header>
      <main className="flex-1 py-16 px-6">
        <div className="max-w-2xl mx-auto bg-white border rounded-2xl shadow-xl p-8" data-testid="signup-success-page">
          <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1
            className="text-2xl font-bold text-center text-zinc-900 mt-5"
            style={{ fontFamily: 'Manrope' }}
          >
            Your workspace is ready{tenant?.name ? ` — ${tenant.name}` : ''} 🎉
          </h1>
          {polling && (
            <div className="mt-3 flex items-center justify-center gap-2 text-amber-700 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…
            </div>
          )}
          {paid && (
            <div className="mt-3 text-center text-sm text-emerald-700">
              ✅ Subscription active.
            </div>
          )}
          <div className="mt-6 bg-zinc-50 border rounded-lg p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Workspace login</div>
            <div className="flex items-stretch border rounded-md overflow-hidden bg-white">
              <input
                readOnly
                value={loginUrl}
                className="flex-1 px-3 py-2 text-sm font-mono"
                data-testid="signup-success-login-url"
              />
              <button
                onClick={copyLink}
                className="px-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs flex items-center gap-1"
                data-testid="signup-success-copy"
              >
                {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
              </button>
            </div>
            {adminEmail && (
              <div className="text-sm text-zinc-700">
                Sign in as: <b>{adminEmail}</b>
              </div>
            )}
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <Link to={loginUrl.replace(window.location.origin, '')} className="flex-1">
              <Button
                className="w-full bg-blue-700 hover:bg-blue-800"
                data-testid="signup-success-goto-login"
              >
                Go to my login <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button variant="outline" className="w-full">Back to home</Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
